import type { CurrencyModel } from "../viewModels/Settings/CurrencyModel";
import convertFromBaseCurrency from "./convertFromBaseCurrency";

export type SummaryCurrencyRow = {
  amount?: number;
  currencyId: number;
  vat?: number | null;
};

export type SummaryCurrencyValues = {
  withVat: number | undefined;
  withoutVat: number | undefined;
  vat: number | undefined;
};

const unknownValues = (): SummaryCurrencyValues => ({ withVat: undefined, withoutVat: undefined, vat: undefined });

export default function getSummaryCurrencyValues(
  rows: Array<SummaryCurrencyRow>,
  currencies: Array<CurrencyModel>,
  baseCurrencyId: number,
  targetCurrencyId: number,
): SummaryCurrencyValues {
  let withVat = 0;
  let withoutVat = 0;
  let vat = 0;

  for (const row of rows) {
    if (row.amount === undefined || row.amount === null) continue;
    if (!Number.isFinite(row.amount)) return unknownValues();

    let amount = row.amount;
    if (amount !== 0 && row.currencyId !== targetCurrencyId) {
      if (row.currencyId !== baseCurrencyId) {
        const sourceCurrency = currencies.find((currency) => currency.id === row.currencyId);
        if (
          !sourceCurrency ||
          sourceCurrency.baseCurrencyId !== baseCurrencyId ||
          !Number.isFinite(sourceCurrency.rate) ||
          sourceCurrency.rate <= 0 ||
          !Number.isFinite(sourceCurrency.nominal) ||
          sourceCurrency.nominal <= 0
        ) {
          return unknownValues();
        }
        amount = (amount * sourceCurrency.rate) / sourceCurrency.nominal;
      }

      if (targetCurrencyId !== baseCurrencyId) {
        const targetCurrency = currencies.find((currency) => currency.id === targetCurrencyId);
        if (!targetCurrency) return unknownValues();
        const convertedAmount = convertFromBaseCurrency(amount, baseCurrencyId, targetCurrency);
        if (convertedAmount === undefined) return unknownValues();
        amount = convertedAmount;
      }
    }

    const vatRate = row.vat ?? 0;
    if (!Number.isFinite(amount) || !Number.isFinite(vatRate) || vatRate < 0) return unknownValues();

    const amountWithoutVat = (amount * 100) / (100 + vatRate);
    withVat += amount;
    withoutVat += amountWithoutVat;
    vat += amount - amountWithoutVat;
  }

  return [withVat, withoutVat, vat].every(Number.isFinite) ? { withVat, withoutVat, vat } : unknownValues();
}
